// Enviar fotos AVULSAS pro acervo (Gestao de Fotos > Acervo), no padrao Master.
//
// Duas regras de produto moram aqui, e as duas foram escolha do Ruan:
//   - PORTAO DURO: a foto so entra se o master sair `completo` (>=2000px no
//     maior lado + fundo transparente de verdade). Quem decide e o backend
//     (POST /api/upload/acervo-master), que nem chega a gravar arquivo quando
//     recusa. Aqui a tela so mostra o motivo.
//   - SEM VINCULO: a foto entra solta no acervo. Amarrar a produto continua
//     sendo o "Aplicar em produtos" do menu ... de cada foto, quando ele quiser.
//
// O motivo da recusa fica NA TELA, item a item, e nao em toast: toast some e o
// motivo nao e recuperavel depois (DA-443). E o operador precisa dele pra saber
// se refaz a foto ou se e outro arquivo.
import { useRef, useState } from 'react';
import { Upload, X, Check, AlertTriangle, Loader2, ImagePlus } from 'lucide-react';
import { Modal } from '../../components/Modal';
import { UpperInput } from '../../components/inputs/UpperInput';
import { encolherSemPerderResolucao } from '../../lib/foto-webp';

// Teto do que trafega, igual ao da ficha e ao multer da rota. Master PNG acima
// disso nao e recusado: vira WebP na mesma resolucao antes de sair (foto-webp.ts).
const LIMITE_BYTES = 5 * 1024 * 1024;

type StatusItem = 'aguardando' | 'enviando' | 'ok' | 'erro';

interface ItemEnvio {
  id: string;
  file: File;
  preview: string;
  nome: string;
  status: StatusItem;
  motivos: string[];
}

interface Props {
  aberto: boolean;
  onFechar: () => void;
  // Chamado ao fechar QUANDO alguma foto entrou — o acervo precisa recarregar.
  onEntraram: () => void;
}

// Nome inicial da foto = nome do arquivo sem a extensao. E so um palpite: o
// campo fica editavel e e o operador quem batiza.
function nomeDoArquivo(f: File): string {
  return f.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim().toUpperCase();
}

export function ModalEnviarFotosAcervo({ aberto, onFechar, onEntraram }: Props) {
  const [itens, setItens] = useState<ItemEnvio[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [arrastando, setArrastando] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Quantas entraram de fato — decide se o acervo recarrega ao fechar.
  const entraramRef = useRef(0);

  function acrescentar(files: FileList | File[] | null) {
    if (!files) return;
    const novos: ItemEnvio[] = Array.from(files)
      .filter((f) => /^image\//i.test(f.type))
      .map((f, i) => ({
        id: `${Date.now()}-${i}-${f.name}`,
        file: f,
        preview: URL.createObjectURL(f),
        nome: nomeDoArquivo(f),
        status: 'aguardando' as StatusItem,
        motivos: [],
      }));
    if (novos.length) setItens((prev) => [...prev, ...novos]);
  }

  function remover(id: string) {
    setItens((prev) => {
      const alvo = prev.find((i) => i.id === id);
      if (alvo) URL.revokeObjectURL(alvo.preview);
      return prev.filter((i) => i.id !== id);
    });
  }

  function trocarNome(id: string, nome: string) {
    setItens((prev) => prev.map((i) => (i.id === id ? { ...i, nome } : i)));
  }

  function marcar(id: string, patch: Partial<ItemEnvio>) {
    setItens((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  }

  async function enviarTodos() {
    const fila = itens.filter((i) => i.status === 'aguardando' || i.status === 'erro');
    if (!fila.length) return;
    setEnviando(true);
    const token = localStorage.getItem('fiodecobre.token');

    // SEQUENCIAL de proposito. Cada foto roda o rembg na VPS (~1,8 GB de RAM e
    // alguns segundos); mandar o lote em paralelo derruba o servico da loja.
    for (const item of fila) {
      marcar(item.id, { status: 'enviando', motivos: [] });
      try {
        // Cabe? sobe intacta. Nao cabe? vira WebP na MESMA resolucao — encolher
        // o lado e justamente o que impediria a foto de atingir o padrao Master.
        const arquivo = await encolherSemPerderResolucao(item.file, LIMITE_BYTES);
        const fd = new FormData();
        fd.append('foto', arquivo);
        fd.append('nome', item.nome.trim());
        const res = await fetch('/api/upload/acervo-master', {
          method: 'POST',
          body: fd,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const dados = await res.json().catch(() => ({} as any));
        if (!res.ok) {
          const motivos: string[] = Array.isArray(dados?.motivos) && dados.motivos.length
            ? dados.motivos
            : [dados?.erro || 'Falha ao enviar esta foto.'];
          marcar(item.id, { status: 'erro', motivos });
          continue;
        }
        entraramRef.current += 1;
        marcar(item.id, { status: 'ok', motivos: [] });
      } catch (e: any) {
        marcar(item.id, { status: 'erro', motivos: [e?.message || 'Falha de conexão ao enviar.'] });
      }
    }
    setEnviando(false);
  }

  function fechar() {
    itens.forEach((i) => URL.revokeObjectURL(i.preview));
    setItens([]);
    if (entraramRef.current > 0) {
      entraramRef.current = 0;
      onEntraram();
    }
    onFechar();
  }

  const pendentes = itens.filter((i) => i.status === 'aguardando' || i.status === 'erro');
  const semNome = pendentes.some((i) => !i.nome.trim());
  const entraram = itens.filter((i) => i.status === 'ok').length;
  const recusadas = itens.filter((i) => i.status === 'erro').length;
  // Fila esvaziada com algo ja no acervo: nao ha mais o que enviar, so fechar.
  const terminou = entraram > 0 && pendentes.length === 0 && !enviando;

  return (
    <Modal
      aberto={aberto}
      onFechar={fechar}
      titulo="Enviar fotos ao acervo"
      largura={720}
      rodape={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted, #6b7280)' }}>
            {entraram > 0 && <strong style={{ color: 'var(--primary)' }}>{entraram} no acervo</strong>}
            {entraram > 0 && recusadas > 0 && ' · '}
            {recusadas > 0 && <strong style={{ color: '#c2410c' }}>{recusadas} recusada(s)</strong>}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Acabou a fila, fechar VIRA a acao principal — entao ele leva o peso
                (botao solido), nao o cinza. */}
            <button
              type="button"
              className={terminou ? 'btn-primario' : 'btn-secundario'}
              onClick={fechar}
              disabled={enviando}
            >
              {entraram > 0 ? 'Concluir' : 'Cancelar'}
            </button>
            {/* O botao de enviar SOME quando nao ha mais nada a enviar. Deixa-lo ali
                desabilitado e ainda dizendo "Enviar" e um controle morto: o operador
                clica, nada acontece, e a leitura e que o sistema travou — foi
                exatamente o que aconteceu no 1o uso real (2026-08-08), com a foto ja
                gravada no acervo e a tela dizendo "1 no acervo" logo ao lado. */}
            {pendentes.length > 0 && (
              <button
                type="button"
                className="btn-primario"
                onClick={enviarTodos}
                disabled={enviando || semNome}
                title={semNome ? 'Toda foto precisa de um nome' : undefined}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
              >
                {enviando ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                {enviando ? 'Enviando...' : `Enviar ${pendentes.length}`}
              </button>
            )}
          </div>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} className="text-sm">
        <p style={{ margin: 0, color: 'var(--text-muted, #6b7280)', fontSize: 13 }}>
          A foto entra <strong>solta</strong> no acervo — sem produto. Pra amarrar a um produto depois, use{' '}
          <strong>Aplicar em produtos</strong> no menu <strong>⋮</strong> da foto.
          <br />
          Só entra no padrão <strong>Master</strong>: pelo menos <strong>2000px</strong> no maior lado e fundo que o
          recorte consiga separar. O que não alcançar é recusado com o motivo — nada fica pela metade.
        </p>

        {/* Area de soltar / escolher arquivos */}
        <div
          onDragOver={(e) => { e.preventDefault(); setArrastando(true); }}
          onDragLeave={() => setArrastando(false)}
          onDrop={(e) => { e.preventDefault(); setArrastando(false); acrescentar(e.dataTransfer.files); }}
          onClick={() => inputRef.current?.click()}
          style={{
            border: `2px dashed ${arrastando ? 'var(--primary)' : 'var(--panel-border, #e5e7eb)'}`,
            background: arrastando ? 'var(--accent-soft, rgba(0,0,0,0.03))' : 'transparent',
            borderRadius: 8,
            padding: '18px 16px',
            textAlign: 'center',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <ImagePlus size={24} style={{ color: 'var(--primary)', marginBottom: 4 }} />
          <div style={{ fontWeight: 600 }}>Arraste as fotos aqui ou clique pra escolher</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted, #6b7280)', marginTop: 2 }}>
            JPG, PNG ou WEBP · pode escolher várias de uma vez
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => { acrescentar(e.target.files); e.target.value = ''; }}
          />
        </div>

        {/* Fila */}
        {itens.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {itens.map((it) => (
              <div
                key={it.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: 8,
                  border: '1px solid var(--panel-border, #e5e7eb)',
                  borderRadius: 8,
                  background: it.status === 'ok' ? 'var(--accent-soft, rgba(0,0,0,0.02))' : 'transparent',
                }}
              >
                <img
                  src={it.preview}
                  alt=""
                  style={{
                    width: 56, height: 56, objectFit: 'contain', background: '#fff',
                    borderRadius: 4, border: '1px solid var(--panel-border, #e5e7eb)', flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <UpperInput
                    value={it.nome}
                    onChange={(v) => trocarNome(it.id, v)}
                    disabled={it.status === 'ok' || enviando}
                    placeholder="NOME DA FOTO (obrigatório)"
                    style={{ width: '100%', fontSize: 13 }}
                  />
                  <div style={{ fontSize: 11, color: 'var(--text-muted, #6b7280)', marginTop: 3 }}>
                    {it.file.name} · {Math.max(1, Math.round(it.file.size / 1024))} KB
                  </div>

                  {it.status === 'erro' && it.motivos.length > 0 && (
                    <div
                      style={{
                        marginTop: 6, padding: '7px 9px', borderRadius: 6,
                        background: '#f97316', color: '#000', fontSize: 12, lineHeight: 1.35,
                      }}
                    >
                      <strong style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                        <AlertTriangle size={13} /> Fora do padrão Master
                      </strong>
                      {it.motivos.map((m, i) => <div key={i}>{m}</div>)}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, paddingTop: 6 }}>
                  {it.status === 'enviando' && <Loader2 size={16} className="animate-spin" style={{ color: 'var(--primary)' }} />}
                  {it.status === 'ok' && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--primary)', fontWeight: 700, fontSize: 12 }}>
                      <Check size={15} /> No acervo
                    </span>
                  )}
                  {it.status !== 'enviando' && it.status !== 'ok' && (
                    <button
                      type="button"
                      onClick={() => remover(it.id)}
                      disabled={enviando}
                      title="Tirar da fila"
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted, #6b7280)', padding: 2 }}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
